// docs/92 HM7: the hall a huge body's sounds ring in. One convolution reverb, built once from a
// made impulse (no file to fetch): stereo noise under an exponential decay, its highs dying faster
// than its lows, so the tail is dark and big — a cavern, not a bathroom. Only what a huge body
// does is sent into it (its voice, its moves, its steps and landings, its debris and fire), at a
// level by its size, so a dragon's roar blooms behind the dry hit while every hero's sound stays
// dry. The send sits after the voice's own gain, so mute and the volume sliders reach the tail.

/** Seconds the tail rings. */
export const HALL_SECONDS = 1.8;
/** How fast it dies (higher, shorter). */
export const HALL_DECAY = 3.2;
/** The hall's return, linear (≈ −6 dB): under it, each voice's send sets how much of it rings. */
export const HALL_RETURN = 0.5;
/** Where the tail's highs are cut (Hz): dark stone, not glass. */
export const HALL_LOWPASS_HZ = 2400;

/**
 * A stereo impulse for `seconds` at `rate`: seeded noise (the same hall on every machine) under
 * `(1 − t)^decay`, a few early reflections in the first 60 ms, and a one-pole low-pass that closes
 * as it decays (the highs die first).
 */
export function hallImpulse(rate: number, seconds = HALL_SECONDS, decay = HALL_DECAY, seed = 83): Float32Array[] {
  const n = Math.max(1, Math.floor(rate * seconds));
  const out: Float32Array[] = [];
  for (let ch = 0; ch < 2; ch++) {
    let s = (seed * 2654435761 + ch * 40503) >>> 0;
    const rnd = () => { s = (Math.imul(s, 1664525) + 1013904223) >>> 0; return s / 0x100000000 * 2 - 1; };
    const d = new Float32Array(n);
    let lp = 0;
    for (let i = 0; i < n; i++) {
      const t = i / n;
      const env = Math.pow(1 - t, decay);
      // the low-pass closes over the tail: bright at the start, dull at the end
      const k = 0.55 - 0.45 * t;
      lp += k * (rnd() - lp);
      d[i] = lp * env;
    }
    // early reflections: a few taps off the near walls, different left and right
    for (const [ms, g] of ch === 0 ? [[11, 0.5], [23, 0.32], [41, 0.22], [57, 0.14]] : [[14, 0.48], [29, 0.3], [37, 0.2], [61, 0.13]]) {
      const at = Math.floor((ms! / 1000) * rate);
      if (at < n) d[at] = (d[at] ?? 0) + g!;
    }
    out.push(d);
  }
  return out;
}

/** How much of a body's sound goes to the hall, by its size: none for one square, more for a 3×3. */
export function hugeSpace(size: number | undefined): number {
  if (!size || size < 2) return 0;
  return size >= 3 ? 0.42 : 0.32;
}
